import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { PMDialog } from "../../../utils/PMDialog";
import { Form, Formik } from "formik";
import { toast } from "react-hot-toast";
import * as Yup from "yup";
import { EmployeeSelector } from "../../../utils/moduleSelectors/employeeSelector";
import { fetchReport, updateReportParticipants } from "../../../../api/service/report";
import { reportParticipantsRole } from "./reportParticipants";
import { workOrderParticipantsStatus } from "../../workOrders/utils";
import { status } from "../../../Utils";

function reportUpdateErrorMessage(res, fallback) {
  if (!res?.response) return fallback;
  const data = res.response?.data;
  if (Array.isArray(data?.errors)) return data.errors.join(", ");
  if (typeof data?.error === "string") return data.error;
  return fallback;
}

export const ReportAccessDialog = ({
  open,
  onClose,
  reportId,
  access,
  onSuccess,
  executingEmployeeId,
  participants = [],
}) => {
  const { t } = useTranslation();
  const isEditing = !!access;
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);

  useEffect(() => {
    if (!open) {
      setShowRoleDialog(false);
      setShowStatusDialog(false);
    }
  }, [open]);

  const selectorExcludeUserIds = useMemo(() => {
    const ids = [];
    if (executingEmployeeId != null && executingEmployeeId !== "") {
      ids.push(executingEmployeeId);
    }
    (participants || []).forEach((p) => {
      if (isEditing && access?.id != null && p.id === access.id) {
        return;
      }
      const uid = p?.user?.id ?? p?.user_id;
      if (uid != null && uid !== "") {
        ids.push(uid);
      }
    });
    return ids;
  }, [executingEmployeeId, participants, isEditing, access?.id]);

  const handleSubmit = async (values, helpers) => {
    try {
      const res = await fetchReport(reportId, { fields: "participants" });
      const body = res?.data;
      const participantData = {
        user_id: values.user_id,
        role: values.role,
        status: values.status || "ASSIGNED",
      };

      let updatedParticipants = [...(body.participants || [])];

      if (isEditing) {
        participantData.id = access.id;
        updatedParticipants = updatedParticipants.map((p) =>
          p.id === access.id ? { ...p, ...participantData } : p
        );
      } else {
        updatedParticipants.push(participantData);
      }

      const saveRes = await updateReportParticipants(
        reportId,
        updatedParticipants
      );

      if (saveRes?.response) {
        toast.error(
          reportUpdateErrorMessage(saveRes, t("report.participants.save_error"))
        );
        helpers.setSubmitting(false);
        return;
      }

      const list = saveRes?.data?.participants;
      if (!Array.isArray(list)) {
        toast.error(t("report.participants.save_error"));
        helpers.setSubmitting(false);
        return;
      }

      if (onSuccess) onSuccess(list);
      toast.success(
        isEditing
          ? t("report.participants.updated")
          : t("report.participants.created")
      );
      helpers.setSubmitting(false);
    } catch (err) {
      console.error(err);
      toast.error(t("report.participants.save_error"));
      helpers.setSubmitting(false);
    }
  };

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title={
        isEditing
          ? t("report.participants.edit")
          : t("report.participants.add")
      }
    >
      <div className="mt-4">
        <Formik
          initialValues={{
            user_id: access?.user?.id || null,
            role: access?.role || "EXECUTOR",
            status: access?.status || "ASSIGNED",
            submit: null,
          }}
          validationContext={{ executingEmployeeId, isEditing }}
          validationSchema={Yup.object({
            user_id: Yup.string()
              .required(t("project.participants.user_required"))
              .test(
                "not-executing-employee",
                t("report.participants.error_is_executing_employee"),
                function (val) {
                  if (this.options.context?.isEditing) return true;
                  const exec = this.options.context?.executingEmployeeId;
                  if (exec == null || exec === "") return true;
                  if (val == null || val === "") return true;
                  return String(val) !== String(exec);
                }
              ),
            role: Yup.string().required(
              t("project.participants.role_required")
            ),
          })}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {(formik) => (
            <>
              <Form>
                <div className="grid grid-cols-6 gap-6">
                  <div className="col-span-6 sm:col-span-6">
                    <EmployeeSelector
                      label={t("report.participants.user")}
                      name="user_id"
                      selected={access?.user}
                      disabled={isEditing}
                      excludeUserIds={selectorExcludeUserIds}
                      {...formik}
                    />
                  </div>
                  <div className="col-span-6 sm:col-span-3">
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      {t("report.participants.role")}
                    </label>
                    {(() => {
                      const selectedRole = reportParticipantsRole.find(
                        (opt) =>
                          String(opt.value) === String(formik.values.role)
                      );
                      return (
                        <button
                          type="button"
                          onClick={() => setShowRoleDialog(true)}
                          className={`w-full rounded-2xl border px-4 py-3 text-left text-sm transition-colors ${
                            selectedRole
                              ? "border-blue-200 bg-blue-50 text-blue-800 ring-1 ring-blue-100"
                              : "border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300 hover:bg-slate-100"
                          }`}
                        >
                          <span className="font-medium">
                            {selectedRole
                              ? t(selectedRole.value)
                              : t("report.participants.choose_role")}
                          </span>
                        </button>
                      );
                    })()}
                    {formik.errors.role && formik.touched.role && (
                      <p className="mt-1 text-sm text-red-600">
                        {formik.errors.role}
                      </p>
                    )}
                  </div>
                  <div className="col-span-6 sm:col-span-3">
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      {t("report.participants.status")}
                    </label>
                    {(() => {
                      const selectedStatus = workOrderParticipantsStatus.find(
                        (opt) =>
                          String(opt.value) === String(formik.values.status)
                      );
                      const statusLabel = selectedStatus
                        ? t(
                            status[selectedStatus.value]?.label ||
                              selectedStatus.label
                          )
                        : t("report.participants.choose_status");
                      return (
                        <button
                          type="button"
                          onClick={() => setShowStatusDialog(true)}
                          className={`w-full rounded-2xl border px-4 py-3 text-left text-sm transition-colors ${
                            selectedStatus
                              ? "border-blue-200 bg-blue-50 text-blue-800 ring-1 ring-blue-100"
                              : "border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300 hover:bg-slate-100"
                          }`}
                        >
                          <span className="font-medium">{statusLabel}</span>
                        </button>
                      );
                    })()}
                  </div>
                </div>

                <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                  <button
                    type="button"
                    className="inline-flex justify-center w-20 rounded-md border border-transparent bg-gray-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                    onClick={onClose}
                  >
                    {t("Cancel")}
                  </button>

                  <button
                    type="submit"
                    disabled={formik.isSubmitting}
                    className="inline-flex align-right justify-center rounded-md border border-transparent bg-emerald-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm disabled:bg-gray-400"
                  >
                    {formik.isSubmitting
                      ? isEditing
                        ? t("report.participants.updating")
                        : t("report.participants.adding")
                      : isEditing
                        ? t("Update")
                        : t("report.participants.add")}
                  </button>
                </div>
              </Form>

              {showRoleDialog &&
                createPortal(
                  <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div
                      className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                      onClick={() => setShowRoleDialog(false)}
                      aria-hidden="true"
                    />
                    <div
                      className="relative w-full max-w-md overflow-hidden rounded-[1.5rem] bg-white shadow-xl"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="border-b border-slate-200 px-5 py-4">
                        <div className="flex items-center justify-between gap-4">
                          <h3 className="text-base font-semibold text-slate-900">
                            {t("report.participants.choose_role")}
                          </h3>
                          <button
                            type="button"
                            onClick={() => setShowRoleDialog(false)}
                            className="rounded-md p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                          >
                            <XMarkIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                      <div className="max-h-[24rem] space-y-2 overflow-y-auto px-3 py-3">
                        {reportParticipantsRole.map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => {
                              formik.setFieldValue("role", opt.value);
                              formik.setTouched({
                                ...formik.touched,
                                role: true,
                              });
                              setShowRoleDialog(false);
                            }}
                            className={`w-full rounded-2xl px-4 py-3 text-left text-sm font-medium transition-colors ${
                              String(formik.values.role) === String(opt.value)
                                ? "bg-blue-50 text-blue-800 ring-1 ring-blue-200"
                                : "bg-slate-50 text-slate-700 hover:bg-slate-100"
                            }`}
                          >
                            {t(opt.value)}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>,
                  document.body
                )}

              {showStatusDialog &&
                createPortal(
                  <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div
                      className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                      onClick={() => setShowStatusDialog(false)}
                      aria-hidden="true"
                    />
                    <div
                      className="relative w-full max-w-md overflow-hidden rounded-[1.5rem] bg-white shadow-xl"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="border-b border-slate-200 px-5 py-4">
                        <div className="flex items-center justify-between gap-4">
                          <h3 className="text-base font-semibold text-slate-900">
                            {t("report.participants.choose_status")}
                          </h3>
                          <button
                            type="button"
                            onClick={() => setShowStatusDialog(false)}
                            className="rounded-md p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                          >
                            <XMarkIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                      <div className="max-h-[24rem] space-y-2 overflow-y-auto px-3 py-3">
                        {workOrderParticipantsStatus.map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => {
                              formik.setFieldValue("status", opt.value);
                              formik.setTouched({
                                ...formik.touched,
                                status: true,
                              });
                              setShowStatusDialog(false);
                            }}
                            className={`w-full rounded-2xl px-4 py-3 text-left text-sm font-medium transition-colors ${
                              String(formik.values.status) === String(opt.value)
                                ? "bg-blue-50 text-blue-800 ring-1 ring-blue-200"
                                : "bg-slate-50 text-slate-700 hover:bg-slate-100"
                            }`}
                          >
                            {t(status[opt.value]?.label || opt.label)}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>,
                  document.body
                )}
            </>
          )}
        </Formik>
      </div>
    </PMDialog>
  );
};
